import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { Carrot } from "@/components/Carrot";
import { ContactForm } from "@/components/ContactForm";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface ContactDialogValue {
  openContact: () => void;
}

const ContactDialogContext = createContext<ContactDialogValue | null>(null);

/**
 * Wraps the app with a single shared contact dialog. Any descendant can open it
 * via useContactDialog(). The form remounts on each open (Radix unmounts closed
 * content), so its state resets automatically.
 */
export function ContactDialogProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const openContact = useCallback(() => setOpen(true), []);

  return (
    <ContactDialogContext.Provider value={{ openContact }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <span className="mb-1 flex size-12 items-center justify-center rounded-2xl bg-carrot-soft">
              <Carrot className="size-7" />
            </span>
            <DialogTitle>Let's talk.</DialogTitle>
            <DialogDescription>
              Tell us about your barn and we'll get you set up, or book a quick demo. Ranchers, breeders,
              trainers and show barns welcome.
            </DialogDescription>
          </DialogHeader>
          <ContactForm />
        </DialogContent>
      </Dialog>
    </ContactDialogContext.Provider>
  );
}

export function useContactDialog(): ContactDialogValue {
  const ctx = useContext(ContactDialogContext);
  if (!ctx) throw new Error("useContactDialog must be used within ContactDialogProvider");
  return ctx;
}
